import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { adminApi } from '@/utils/api';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useNotify } from '@/context/NotificationContext';

export const DEFAULT_TEXT_COLOR = '#FFFFFF';

/** #RRGGBB → rgba() so secondary lines can reuse the same hue at lower opacity. */
export const withAlpha = (hex: string, alpha: number): string => {
    const clean = (hex || '').replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const n = parseInt(full, 16);
    if (Number.isNaN(n) || full.length !== 6) return `rgba(255,255,255,${alpha})`;
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

const PRESETS = [
    { hex: '#FFFFFF', label: 'أبيض' },
    { hex: '#0F172A', label: 'أسود' },
    { hex: '#3390EC', label: 'أزرق' },
    { hex: '#10B981', label: 'أخضر' },
    { hex: '#F59E0B', label: 'برتقالي' },
    { hex: '#F43F5E', label: 'أحمر' },
    { hex: '#A855F7', label: 'بنفسجي' },
    { hex: '#22D3EE', label: 'سماوي' },
    { hex: '#94A3B8', label: 'رمادي' },
];

const PANEL_W = 236;
const PANEL_H = 178;

/**
 * Reads/saves the text colour of a banner slot. The value lives in
 * `system_configuration` under a `bg_*` key, so it ships with every other
 * tile background through GET /api/home/profile-tiles-bg.
 */
export const useBannerTextColor = (colorKey: string) => {
    const { user, tileBgs, fetchTileBgs } = useStore();
    const { showNotification } = useNotify();
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation('common');
    const [busy, setBusy] = useState(false);

    const isAdmin = Boolean(user?.role === 'admin' || String(user?.telegram_id) === '601287447' || (user as any)?.is_admin);
    const color: string = (tileBgs as Record<string, string>)?.[colorKey] || DEFAULT_TEXT_COLOR;

    const save = useCallback(async (hex: string) => {
        setBusy(true);
        try {
            const res = await adminApi.setProfileTileBgs({ [colorKey]: hex });
            const data = await res.json().catch(() => ({}));
            if (!data?.success) throw new Error(data?.error || 'save failed');
            await fetchTileBgs(true);
            showNotification(tc('success'), t('textColorSaved'), 'success');
        } catch (err: any) {
            console.error('[BannerTextColor] save failed', err);
            showNotification(tc('error'), tc('error'), 'error');
        } finally {
            setBusy(false);
        }
    }, [colorKey, fetchTileBgs, showNotification, t, tc]);

    return { color, isAdmin, busy, save };
};

/**
 * Admin-only colour swatch button. The picker is a small popover anchored to the
 * button itself (portalled to <body> so the banner's overflow cannot clip it) —
 * no full-screen dialog, and tapping a colour applies it immediately.
 */
export const BannerTextColorControl: React.FC<{ colorKey: string; className?: string; onOpenChange?: (open: boolean) => void }> = ({ colorKey, className = '', onOpenChange }) => {
    const { color, isAdmin, busy, save } = useBannerTextColor(colorKey);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(color);
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const btnRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<{ x: number; y: number } | null>(null);
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation('common');
    const { impactOccurred } = useHapticFeedback();

    const setOpenBoth = useCallback((v: boolean) => { setOpen(v); onOpenChange?.(v); }, [onOpenChange]);

    const place = useCallback(() => {
        const r = btnRef.current?.getBoundingClientRect();
        if (!r) return;
        const left = Math.max(8, Math.min(r.left - 6, window.innerWidth - PANEL_W - 8));
        const below = r.bottom + 6;
        const top = below + PANEL_H > window.innerHeight - 8
            ? Math.max(8, r.top - PANEL_H - 6)
            : below;
        setPos({ top, left });
        anchorRef.current = { x: r.x, y: r.y };
    }, []);

    useLayoutEffect(() => { if (open) place(); }, [open, place]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return;
            setOpenBoth(false);
        };
        const onScroll = () => {
            // Dismiss only when the button itself moves away (real scrolling or rotation).
            // A plain resize (e.g. a scrollbar appearing) just re-anchors the popover,
            // otherwise saving a colour would close the picker mid-selection.
            const r = btnRef.current?.getBoundingClientRect();
            const a = anchorRef.current;
            if (!r || !a) return;
            if (Math.abs(r.x - a.x) > 2 || Math.abs(r.y - a.y) > 2) setOpenBoth(false);
        };
        document.addEventListener('pointerdown', onDown, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', place);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', place);
        };
    }, [open, setOpenBoth, place]);

    if (!isAdmin) return null;

    const commit = (hex: string) => {
        setDraft(hex);
        if (!busy) save(hex);
    };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                title={t('textColorTitle')}
                aria-label={t('textColorTitle')}
                onClick={(e) => {
                    e.stopPropagation();
                    impactOccurred('light');
                    setDraft(color);
                    setOpenBoth(!open);
                }}
                className={`absolute z-30 w-6 h-6 rounded-full bg-black/65 border border-white/30 backdrop-blur-md flex items-center justify-center hover:bg-black/90 active:scale-90 transition-all cursor-pointer shadow-lg ${className}`}
            >
                <Palette size={12} className="text-white/90" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-black/60" style={{ background: color }} />
            </button>

            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {open && (
                        <motion.div
                            ref={panelRef}
                            dir="ltr"
                            initial={{ opacity: 0, scale: 0.94, y: -4 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: -4 }}
                            transition={{ type: 'spring', damping: 28, stiffness: 460, mass: 0.4 }}
                            style={{ top: pos.top, left: pos.left, width: PANEL_W }}
                            onClick={(e) => e.stopPropagation()}
                            className="fixed z-[210] rounded-2xl bg-tg-secondary-bg border border-white/12 shadow-[0_18px_40px_rgba(0,0,0,.55)] p-2.5"
                        >
                            <div className="flex items-center justify-between mb-2 px-0.5">
                                <span className="text-[10px] font-black uppercase tracking-wider text-white/60">{t('textColorTitle')}</span>
                                <button
                                    type="button"
                                    title={tc('reset')}
                                    aria-label={tc('reset')}
                                    onClick={() => { impactOccurred('light'); setDraft(DEFAULT_TEXT_COLOR); save(DEFAULT_TEXT_COLOR); }}
                                    className="text-white/50 hover:text-white active:scale-90 transition-all"
                                >
                                    <RotateCcw size={11} />
                                </button>
                            </div>

                            <div className="grid grid-cols-5 gap-1.5">
                                {PRESETS.map(p => (
                                    <button
                                        key={p.hex}
                                        type="button"
                                        title={p.label}
                                        aria-label={p.label}
                                        onClick={() => { impactOccurred('light'); commit(p.hex); }}
                                        className={`w-full aspect-square rounded-lg border transition-all active:scale-90 ${
                                            draft.toUpperCase() === p.hex ? 'border-white ring-2 ring-white/40' : 'border-white/15'
                                        }`}
                                        style={{ background: p.hex }}
                                    />
                                ))}
                            </div>

                            <label className="mt-2 flex items-center gap-2 rounded-xl bg-white/[0.05] border border-white/10 px-2 py-1.5 cursor-pointer">
                                <input
                                    type="color"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onBlur={() => { if (draft.toUpperCase() !== color.toUpperCase()) commit(draft); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }}
                                    className="w-6 h-6 rounded-md bg-transparent border-0 cursor-pointer p-0"
                                />
                                <span className="text-[10px] font-bold text-white/80" dir="ltr">{draft.toUpperCase()}</span>
                                <span className="text-[10px] font-semibold text-white/45 ms-auto">{t('customColor')}</span>
                            </label>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
};

export default BannerTextColorControl;